import router from '@/router';
import { useAuthStore } from '@/stores/auth';
import { useNotificationStore } from '@/stores/notification';
import { Network } from '@capacitor/network';
import { $log } from '@/common/api/log.service';
import { LOG_ERROR, LOG_WARNING, LOG_INFO } from '@/common/log/log';

// Flips to true when the page is being unloaded or hidden so we can suppress
// logging for requests the browser cancels on our behalf. Reset on visibility
// restore so a backgrounded tab returning to focus resumes normal logging.
let pageUnloading = false;
if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => {
        pageUnloading = true;
    });
    window.addEventListener('beforeunload', () => {
        pageUnloading = true;
    });
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            pageUnloading = false;
        }
    });
}

function redactHeaders(headers) {
    // Handles both plain objects and axios 1.x AxiosHeaders instances.
    // AxiosHeaders doesn't store keys as own enumerable properties, so a
    // naive spread returns {} — we have to normalize via toJSON / entries.
    let normalized = {};
    if (headers && typeof headers.toJSON === 'function') {
        normalized = { ...headers.toJSON() };
    } else if (headers && typeof headers === 'object') {
        normalized = { ...headers };
    }
    for (const key of Object.keys(normalized)) {
        if (key.toLowerCase() === 'authorization') {
            normalized[key] = '[redacted]';
        }
    }
    return normalized;
}

// A 403 on a GET of /User or /User/{id} while the viewer carries the
// restrictToSharedLocations flag (#4582/#4616) is the shared-location access
// policy working as designed — the target simply isn't at one of the viewer's
// locations. Surfacing the generic permission snackbar for it reads as an
// error, so these are suppressed (the request is still logged at warning
// level below). Non-GET methods and other resource types keep the snackbar:
// a restricted user failing to WRITE a User, or being denied something
// unrelated, is a real permission problem worth showing.
export function isLocationRestricted403(err, authStore) {
    if (err?.response?.status !== 403) return false;
    if (authStore.user?.user?.restrictToSharedLocations !== true) return false;
    const method = (err.config?.method || '').toLowerCase();
    if (method && method !== 'get') return false;
    const url = err.config?.url || '';
    // Match "User" as an exact path segment (User?... , /User/abc, full URLs) —
    // not other resources that merely start with the word (e.g. /UserInvite).
    return /(^|\/)User(\/[^/?#]+)?([?#]|$)/.test(url);
}

function classifyLogSeverity(err, isOffline = false) {
    // Returns a LOG_* level, or null to skip logging entirely.
    if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.__CANCEL__) {
        return null;
    }
    // Skip noise from requests cancelled by page navigation/unload, and
    // from requests that fail because the user is fully offline (the user
    // already gets a notification — logging is redundant).
    if (pageUnloading) return null;
    if (isOffline && !err?.response) return null;

    const status = err?.response?.status;
    if (!status) {
        // No response: network error, timeout, DNS failure, CORS, etc.
        // The browser deliberately hides the cause, so we can't tell a
        // transient blip from a server outage. Log at warning level so
        // individual entries aren't treated as actionable errors.
        return LOG_WARNING;
    }
    if (status >= 500) return LOG_ERROR;
    if (status === 401 || status === 403 || status === 404) return LOG_WARNING;
    if (status >= 400) return LOG_ERROR;
    return LOG_INFO;
}

export async function bnHttpErrorHandler(err, timeout = 25000) {
    // Add guard against recursive error handling
    if (err?.config?.url?.includes('/log')) {
        // Skip error handling for logging endpoints to prevent infinite loops
        return Promise.reject(err);
    }

    const authStore = useAuthStore();
    let notifications = [];
    console.error(err);

    let status;
    try {
        status = await Network?.getStatus();
    } catch {
        /* empty */
    }
    const isOffline = !!(status && !status.connected && status.connectionType === 'none');
    if (isOffline) {
        notifications.push({
            message: 'Please check your internet connection',
            color: 'error',
        });
    } else if (err?.message?.msg === 'Network Error' || err?.message === 'Network Error') {
        notifications.push({
            message: 'Cannot reach the server, please check your internet connection. Please try again later or contact support if problem persists.',
            color: 'error',
        });
    } else if (err.message === 'timeout of ' + timeout + 'ms exceeded') {
        notifications.push({
            message: 'The server took too long to respond. You can wait a bit and try again or reload this page.  If this problem persists please contact BestNotes for support.',
            color: 'error',
        });
    } else if (err?.response?.status === 401 && ['Invalid JWT Token', 'JWT is expired', 'Expired JWT Token'].includes(err?.response?.data?.message)) {
        if (authStore.acquireAuthRedirect()) {
            await router.push({
                name: 'lockout',
                params: { lockoutDescription: 'Your screen has been locked since logging in on another device.  Only 1 browser can be logged in at a time.' },
            });
        }
    } else if (err?.config && Object.prototype.hasOwnProperty.call(err.config, 'errorHandle') && err.config.errorHandle === false) {
        try {
            const severity = classifyLogSeverity(err, isOffline);
            if (!err?.config?.skipLogging && err.response && severity) {
                const errorTrace = `Status: ${err.response.status}\nURL: ${err.response.config?.url}\nMethod: ${err.response.config?.method}\nRequest Headers: ${JSON.stringify(redactHeaders(err.config?.headers), null, 2)}\nResponse Data: ${JSON.stringify(err.response.data, null, 2)}`;
                $log.log(severity, 'HTTP Request Failed', err.message, errorTrace);
            }
        } catch {
            /* prevent logging failures from blocking error propagation */
        }
        return Promise.reject(err);
    } else if (!err.response) {
        // network error (wasn't able to connect to server)
        notifications.push({
            message: 'There was a problem with the server.  Please try again.',
            color: 'error',
        });
    } else if (isLocationRestricted403(err, authStore)) {
        // Expected denial for a shared-location-restricted viewer — no snackbar.
        // Falls through to the logging block below.
    } else if ((err.response.status === 401 || err.response.status === 403) && authStore.isAuthenticated) {
        notifications.push({
            message: 'You do not have permission to view this content.',
            color: 'error',
        });
    } else if (err.response.data.errors && err.response.data.errors.length) {
        err.response.data.errors.forEach((err) => {
            notifications.push({
                // Entries can be objects ({ userMessage, message }) or plain strings.
                message: err.userMessage || err.message || err,
                color: 'error',
            });
        });
    } else if (err.response.status >= 500) {
        notifications.push({
            message: 'There was a problem with the server.  Please try again.',
            color: 'error',
        });
    } else if (err.response.status >= 400) {
        notifications.push({
            message: 'Bad request. Please try again.  If problem persists log off then try again.',
            color: 'error',
        });
    }

    const notificationStore = useNotificationStore();

    notificationStore.add(notifications);

    // Modify the logging section to include additional safety checks
    try {
        const severity = classifyLogSeverity(err, isOffline);
        if (!err?.config?.skipLogging && severity) {
            let errorSummary = 'HTTP Request Failed';
            let errorDescription = err.message;
            let errorTrace = '';

            const route = router.currentRoute?.value?.fullPath;
            if (route) errorTrace += `Route: ${route}\n`;
            if (err?.code) errorTrace += `Code: ${err.code}\n`;

            // Build detailed error trace
            if (err.response) {
                // Server responded with error
                errorTrace += `Status: ${err.response.status}\n`;
                errorTrace += `Status Text: ${err.response.statusText}\n`;
                errorTrace += `URL: ${err.response.config?.url}\n`;
                errorTrace += `Method: ${err.response.config?.method}\n`;
                errorTrace += `Request Headers: ${JSON.stringify(redactHeaders(err.config?.headers), null, 2)}\n`;
                errorTrace += `Response Headers: ${JSON.stringify(err.response.headers, null, 2)}\n`;
                errorTrace += `Response Data: ${JSON.stringify(err.response.data, null, 2)}`;
            } else if (err.request) {
                // Request made but no response
                const url = err.config?.url ?? err.request?.responseURL ?? err.request?._url ?? 'unknown';
                const method = err.config?.method ?? 'unknown';
                errorTrace += `Request made but no response received\n`;
                errorTrace += `URL: ${url}\n`;
                errorTrace += `Method: ${method}`;
                if (err.stack) errorTrace += `\nStack: ${err.stack}`;
            } else {
                // Error in request setup
                errorTrace += err.stack || err.toString();
            }

            $log.log(severity, errorSummary, errorDescription, errorTrace);
        }
    } catch (loggingError) {
        // If logging fails, just console.error and continue
        console.error('Failed to log error:', loggingError);
    }

    return Promise.reject(err);
}
